import { Badge } from '@oysterjs/ui/badge';
import type { ColumnDef } from '@oysterjs/ui/data-table';
import {
  ClientListFieldsFragment,
  ClientSortField,
  ClientSortInput,
  SortDirection
} from '~/api/graphql';
import { dateFormatter, formatRelativeTime } from '~/components/table-formatters';
import { integrationName } from '~/lib/integrations';

// Only name and last activity are sortable: those are the two keyset indexes the
// clients query has. Sorting on owner or email would be a sequential scan.
export type ClientSortKey = 'name' | 'lastActivity';

const SORT_KEY_TO_FIELD: Record<ClientSortKey, ClientSortField> = {
  name: ClientSortField.Name,
  lastActivity: ClientSortField.UpdatedAt
};

/** Map UI sort key + direction to GraphQL ClientSortInput. */
export function clientSortToClientSortInput(
  sortBy: ClientSortKey,
  sortDirection: 'asc' | 'desc'
): ClientSortInput {
  return {
    field: SORT_KEY_TO_FIELD[sortBy],
    direction: sortDirection === 'asc' ? SortDirection.Asc : SortDirection.Desc
  };
}

/**
 * Renders the commercial/personal flags. Both can be true -- a sole proprietor
 * holding both lines -- and both can be false, which is the ordinary state for a
 * client the backfill only knows a name for. Neither case is an error, so this
 * returns an em dash rather than nothing when the lines are unknown.
 */
export const ClientLinesBadges = (props: { isCommercial: boolean; isPersonal: boolean }) => {
  if (!props.isCommercial && !props.isPersonal) {
    return <Dash />;
  }
  return (
    <div className="flex gap-x-1">
      {props.isCommercial && <Badge color="sky">Commercial</Badge>}
      {props.isPersonal && <Badge color="purple">Personal</Badge>}
    </div>
  );
};

/**
 * `APPLICATION_FORM` reads as a database value; "Application form" reads as a
 * type. Sentence case rather than title case, which is what titleCase in
 * calls-columns.tsx does -- the two conventions are not interchangeable.
 */
export const humanizeEnum = (value: string): string => {
  const words = value.replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export const formatAddress = (address: {
  line1: string;
  line2?: string | null;
  city: string;
  subZone?: string | null;
  zone: string;
  postalCode: string;
}): string | null => {
  // Every part is individually optional in storage and reported as an empty
  // string when unknown, so an address can arrive with nothing worth printing.
  const street = [address.line1, address.line2].filter(Boolean).join(', ');
  const region = [address.city, address.zone].filter(Boolean).join(', ');
  const parts = [street, region, address.postalCode].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
};

/**
 * A dash rather than an empty cell for missing contact details.
 *
 * 61% of agencies have no synced AMS account data, so for a large share of
 * clients every column but the name is blank. An explicit placeholder makes that
 * read as "not on file" rather than as a broken row.
 */
export const Dash = () => <span className="text-neutral-400 dark:text-neutral-500">&mdash;</span>;

export const OrDash = (props: { value?: string | null; className?: string }) =>
  props.value ? <span className={props.className}>{props.value}</span> : <Dash />;

/** The ways to reach a contact, or null when none are on file. */
export const contactLine = (contact: { email?: string | null; phone?: string | null }) =>
  [contact.email, contact.phone].filter(Boolean).join(' \u00b7 ') || null;

/** How much hangs off a client, which is how two records for one client are told apart. */
export const countLine = (client: {
  associationCounts: { workspaces: number; amsAccounts: number; notes: number };
}) => {
  const counts = client.associationCounts;
  const parts = [
    `${counts.workspaces} ${counts.workspaces === 1 ? 'workspace' : 'workspaces'}`,
    `${counts.amsAccounts} AMS ${counts.amsAccounts === 1 ? 'account' : 'accounts'}`,
    `${counts.notes} ${counts.notes === 1 ? 'note' : 'notes'}`
  ];
  return parts.join(' · ');
};

/**
 * Lines beside the name and email beneath it, rather than columns of their own:
 * both are unknown for most of a book that has not been enriched, and
 * mostly-empty columns read worse than a detail that is sometimes a dash. The
 * badges are left out rather than dashed when unknown, since the name already
 * fills the line.
 */
const NameCell = (props: { client: ClientListFieldsFragment }) => {
  const { client } = props;
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-medium">{client.name}</span>
        {(client.isCommercial || client.isPersonal) && (
          <ClientLinesBadges isCommercial={client.isCommercial} isPersonal={client.isPersonal} />
        )}
      </div>
      <div className="truncate text-xs text-neutral-500 dark:text-neutral-400">
        <OrDash value={client.email} />
      </div>
    </div>
  );
};

const RecentWorkCell = (props: { workspace: ClientListFieldsFragment['latestWorkspace'] }) => {
  const { workspace } = props;
  if (!workspace) {
    return <Dash />;
  }
  const updatedAt = new Date(workspace.updatedAt);
  return (
    <div className="flex max-w-xs items-center gap-x-1.5">
      <span className="truncate">{workspace.title || 'Untitled workspace'}</span>
      <span aria-hidden="true" className="text-neutral-400 dark:text-neutral-500">
        &middot;
      </span>
      <time
        dateTime={updatedAt.toISOString()}
        title={dateFormatter.format(updatedAt)}
        className="shrink-0 text-neutral-500 dark:text-neutral-400"
      >
        {formatRelativeTime(updatedAt)}
      </time>
    </div>
  );
};

/**
 * Columns for the Clients list.
 *
 * The brief also asks for CSR, premium, line of business and policy number.
 * None of those exist yet -- there is no policy model and no CSR role
 * assignment -- so they are left out rather than shown as permanently empty.
 */
/**
 * Which AMS a client resolves to, if any. Names the AMS alone and counts the
 * rest: the account's own name belongs to the profile, and a column of them
 * would resolve an entity per row.
 */
export const ClientAmsAccountCell = (props: { links: ClientListFieldsFragment['amsAccounts'] }) => {
  const primary = props.links.find((link) => link.isPrimary) ?? props.links[0];
  if (!primary) {
    return <Dash />;
  }
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="truncate">{integrationName(primary.integrationType)}</span>
      {props.links.length > 1 && <Badge color="zinc">+{props.links.length - 1}</Badge>}
    </div>
  );
};

export const CLIENT_COLUMNS: ColumnDef<ClientListFieldsFragment, ClientSortKey>[] = [
  {
    id: 'name',
    header: 'Name',
    sortKey: 'name',
    renderCell: (client) => <NameCell client={client} />
  },
  {
    id: 'recentWork',
    header: 'Recent work',
    renderCell: (client) => <RecentWorkCell workspace={client.latestWorkspace} />
  },
  {
    id: 'owner',
    header: 'Owner',
    renderCell: (client) => <OrDash value={client.agencyUserOwner?.name} />
  },
  {
    id: 'amsAccount',
    header: 'AMS',
    renderCell: (client) => <ClientAmsAccountCell links={client.amsAccounts} />
  },
  {
    id: 'lastActivity',
    header: 'Last activity',
    sortKey: 'lastActivity',
    renderCell: (client) => dateFormatter.format(new Date(client.updatedAt))
  }
];
